import { useEffect, useState } from "react";
import { ZoomImg } from "../components/ZoomImg";
import { useSeo } from "../lib/useSeo";
import { FadeIn } from "../components/FadeIn";
import { PageHeader } from "../components/PageHeader";
import { Section, Overline } from "../components/Primitives";
import { IMAGES } from "../data/content";
import { getBoardMembers } from "../lib/api";
import { useContent } from "../context/ContentContext";

export default function AdvisoryBoard() {
  useSeo("Advisory Board", "Meet the advisory board guiding Asana Memorial Foundation.");
  const { t } = useContent();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    getBoardMembers().then(setMembers).catch(() => setMembers([]));
  }, []);

  return (
    <div data-testid="advisory-board-page">
      <PageHeader
        eyebrow={t("board.header.eyebrow")}
        title={t("board.header.title")}
        intro={t("board.header.intro")}
        image={IMAGES.mentorGroup}
      />

      {/* Overview */}
      <Section className="bg-amf-cream">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <FadeIn>
            <Overline>Board Overview</Overline>
            <p className="font-lora text-lg text-amf-charcoal/85 mt-5 leading-relaxed">
              The Board of Asana Memorial Foundation is responsible for providing
              policy direction, institutional oversight, and strategic guidance.
              Through their diverse expertise and commitment to service, Board
              members help strengthen the Foundation's governance, ensure
              transparency, and advance its long-term objectives.
            </p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <Overline>Why Our Advisory Board Matters</Overline>
            <p className="font-lora text-lg text-amf-charcoal/85 mt-5 leading-relaxed">
              The Asana Memorial Foundation's Advisory Board exists to help us
              achieve greater impact. Our advisors are distinguished leaders and
              professionals who generously contribute their expertise, networks,
              and strategic insights to strengthen the Foundation's mission and
              work. Their guidance supports innovation, accountability, and
              strategic growth, ensuring that the Foundation continues to make a
              lasting difference.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Members */}
      <Section className="bg-[#F4F6F8]">
        <FadeIn className="mb-12">
          <Overline>Advisory Board Members</Overline>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
          {members.map((m, i) => (
            <FadeIn
              key={m.id}
              delay={(i % 2) * 0.1}
              data-testid="board-member-card"
              className="flex flex-col sm:flex-row gap-6"
            >
              <ZoomImg
                src={m.image}
                alt={m.name}
                loading="lazy"
                className="w-28 h-28 rounded-full object-cover shrink-0 ring-1 ring-amf-gold/40 amf-img"
              />
              <div>
                <h3 className="font-playfair text-2xl text-amf-navy leading-tight">
                  {m.name}
                </h3>
                <p className="amf-overline text-amf-burgundy mt-2">{m.role}</p>
                <p className="font-lora text-amf-charcoal/80 mt-4 leading-relaxed text-[0.95rem]">
                  {m.bio}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
}
